import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { map, distinctUntilChanged, debounceTime } from 'rxjs/operators';
import { JobService } from './job.service';
import {
  JobCategoryWithCount,
  JobPositionWithCount,
  JobEducationLevelWithCount,
  JobEmploymentTypeWithCount,
  JobSalaryPeriod,
  JobCompanyWithCount,
} from '../models/job.model';

export interface SalaryFilter {
  minSalary: number;
  maxSalary: number;
  periodKey: string;
}

export interface FilterState {
  selectedCategoryIDs: number[];
  selectedPositionIDs: number[];
  selectedEducationLevelIDs: number[];
  selectedEmploymentTypeIDs: number[];
  selectedCompanyIDs: number[];
  salaryFilter: SalaryFilter;
  searchKeyword: string;
  searchLocation: string;
  searchLatitude: number | null;
  searchLongitude: number | null;
  searchRadius: number | null;
}

export interface FiltersData {
  categories: JobCategoryWithCount[];
  positions: JobPositionWithCount[];
  educationLevels: JobEducationLevelWithCount[];
  employmentTypes: JobEmploymentTypeWithCount[];
  salaryPeriods: JobSalaryPeriod[];
  companies: JobCompanyWithCount[];
}

@Injectable({
  providedIn: 'root',
})
export class FilterStateService {
  // Private state subjects
  private selectedCategoryIDsSubject = new BehaviorSubject<number[]>([]);
  private selectedPositionIDsSubject = new BehaviorSubject<number[]>([]);
  private selectedEducationLevelIDsSubject = new BehaviorSubject<number[]>([]);
  private selectedEmploymentTypeIDsSubject = new BehaviorSubject<number[]>([]);
  private selectedCompanyIDsSubject = new BehaviorSubject<number[]>([]);
  private salaryFilterSubject = new BehaviorSubject<SalaryFilter>({
    minSalary: 0,
    maxSalary: 0,
    periodKey: 'month'
  });
  private searchKeywordSubject = new BehaviorSubject<string>('');
  private searchLocationSubject = new BehaviorSubject<string>('');
  private searchLatitudeSubject = new BehaviorSubject<number | null>(null);
  private searchLongitudeSubject = new BehaviorSubject<number | null>(null);
  private searchRadiusSubject = new BehaviorSubject<number | null>(null);
  private filtersDataSubject = new BehaviorSubject<FiltersData>({
    categories: [],
    positions: [],
    educationLevels: [],
    employmentTypes: [],
    salaryPeriods: [],
    companies: [],
  });

  // Public observables
  public selectedCategoryIDs$ = this.selectedCategoryIDsSubject.asObservable();
  public selectedPositionIDs$ = this.selectedPositionIDsSubject.asObservable();
  public selectedEducationLevelIDs$ = this.selectedEducationLevelIDsSubject.asObservable();
  public selectedEmploymentTypeIDs$ = this.selectedEmploymentTypeIDsSubject.asObservable();
  public selectedCompanyIDs$ = this.selectedCompanyIDsSubject.asObservable();
  public salaryFilter$ = this.salaryFilterSubject.asObservable();
  public searchKeyword$ = this.searchKeywordSubject.asObservable();
  public searchLocation$ = this.searchLocationSubject.asObservable();
  public searchLatitude$ = this.searchLatitudeSubject.asObservable();
  public searchLongitude$ = this.searchLongitudeSubject.asObservable();
  public searchRadius$ = this.searchRadiusSubject.asObservable();
  public filtersData$ = this.filtersDataSubject.asObservable();

  // Combined filter state
  public filterState$: Observable<FilterState> = combineLatest([
    this.selectedCategoryIDs$,
    this.selectedPositionIDs$,
    this.selectedEducationLevelIDs$,
    this.selectedEmploymentTypeIDs$,
    this.selectedCompanyIDs$,
    this.salaryFilter$,
    this.searchKeyword$,
    this.searchLocation$,
    this.searchLatitude$,
    this.searchLongitude$,
    this.searchRadius$,
  ]).pipe(
    map(([selectedCategoryIDs, selectedPositionIDs, selectedEducationLevelIDs, selectedEmploymentTypeIDs, selectedCompanyIDs, salaryFilter, searchKeyword, searchLocation, searchLatitude, searchLongitude, searchRadius]) => ({
      selectedCategoryIDs,
      selectedPositionIDs,
      selectedEducationLevelIDs,
      selectedEmploymentTypeIDs,
      selectedCompanyIDs,
      salaryFilter,
      searchKeyword,
      searchLocation,
      searchLatitude,
      searchLongitude,
      searchRadius,
    }))
  );

  constructor(private jobService: JobService) {
    // Subscribe to filter changes to reload filter data dynamically
    // This also handles the initial load with default empty values
    combineLatest([
      this.selectedCategoryIDs$,
      this.selectedPositionIDs$,
      this.selectedEducationLevelIDs$,
      this.selectedEmploymentTypeIDs$,
      this.salaryFilter$
    ])
      .pipe(
        debounceTime(300), // Debounce to avoid too many API calls
        distinctUntilChanged((prev, curr) =>
          JSON.stringify(prev) === JSON.stringify(curr)
        )
      )
      .subscribe(([categoryIDs, positionIDs, educationLevelIDs, employmentTypeIDs, salaryFilter]) => {
        this.loadFilters(categoryIDs, positionIDs, educationLevelIDs, employmentTypeIDs, salaryFilter);
      });
  }

  // Actions to update filter state
  toggleCategory(categoryID: number): void {
    const current = this.selectedCategoryIDsSubject.value;
    const index = current.indexOf(categoryID);

    if (index > -1) {
      // Remove if already selected
      this.selectedCategoryIDsSubject.next(
        current.filter(id => id !== categoryID)
      );
    } else {
      // Add if not selected
      this.selectedCategoryIDsSubject.next([...current, categoryID]);
    }
  }

  togglePosition(positionID: number): void {
    const current = this.selectedPositionIDsSubject.value;
    const index = current.indexOf(positionID);

    if (index > -1) {
      // Remove if already selected
      this.selectedPositionIDsSubject.next(
        current.filter(id => id !== positionID)
      );
    } else {
      // Add if not selected
      this.selectedPositionIDsSubject.next([...current, positionID]);
    }
  }

  toggleEducationLevel(educationLevelID: number): void {
    const current = this.selectedEducationLevelIDsSubject.value;
    const index = current.indexOf(educationLevelID);

    if (index > -1) {
      // Remove if already selected
      this.selectedEducationLevelIDsSubject.next(
        current.filter(id => id !== educationLevelID)
      );
    } else {
      // Add if not selected
      this.selectedEducationLevelIDsSubject.next([...current, educationLevelID]);
    }
  }

  toggleEmploymentType(employmentTypeID: number): void {
    const current = this.selectedEmploymentTypeIDsSubject.value;
    const index = current.indexOf(employmentTypeID);

    if (index > -1) {
      // Remove if already selected
      this.selectedEmploymentTypeIDsSubject.next(
        current.filter(id => id !== employmentTypeID)
      );
    } else {
      // Add if not selected
      this.selectedEmploymentTypeIDsSubject.next([...current, employmentTypeID]);
    }
  }

  toggleCompany(companyID: number): void {
    const current = this.selectedCompanyIDsSubject.value;
    const index = current.indexOf(companyID);

    if (index > -1) {
      // Remove if already selected
      this.selectedCompanyIDsSubject.next(
        current.filter(id => id !== companyID)
      );
    } else {
      // Add if not selected
      this.selectedCompanyIDsSubject.next([...current, companyID]);
    }
  }

  setSalaryFilter(minSalary: number, maxSalary: number, periodKey: string): void {
    this.salaryFilterSubject.next({ minSalary, maxSalary, periodKey });
  }

  setSearchKeyword(keyword: string): void {
    this.searchKeywordSubject.next(keyword);
  }

  setSearchLocation(location: string, latitude: number | null = null, longitude: number | null = null, radius: number | null = null): void {
    this.searchLocationSubject.next(location);
    this.searchLatitudeSubject.next(latitude);
    this.searchLongitudeSubject.next(longitude);
    this.searchRadiusSubject.next(radius);
  }

  clearAllFilters(): void {
    this.selectedCategoryIDsSubject.next([]);
    this.selectedPositionIDsSubject.next([]);
    this.selectedEducationLevelIDsSubject.next([]);
    this.selectedEmploymentTypeIDsSubject.next([]);
    this.selectedCompanyIDsSubject.next([]);
    this.salaryFilterSubject.next({ minSalary: 0, maxSalary: 0, periodKey: 'month' });
    this.searchKeywordSubject.next('');
    this.searchLocationSubject.next('');
    this.searchLatitudeSubject.next(null);
    this.searchLongitudeSubject.next(null);
    this.searchRadiusSubject.next(null);
  }

  // Get current values (synchronously)
  getCurrentFilterState(): FilterState {
    return {
      selectedCategoryIDs: this.selectedCategoryIDsSubject.value,
      selectedPositionIDs: this.selectedPositionIDsSubject.value,
      selectedEducationLevelIDs: this.selectedEducationLevelIDsSubject.value,
      selectedEmploymentTypeIDs: this.selectedEmploymentTypeIDsSubject.value,
      selectedCompanyIDs: this.selectedCompanyIDsSubject.value,
      salaryFilter: this.salaryFilterSubject.value,
      searchKeyword: this.searchKeywordSubject.value,
      searchLocation: this.searchLocationSubject.value,
      searchLatitude: this.searchLatitudeSubject.value,
      searchLongitude: this.searchLongitudeSubject.value,
      searchRadius: this.searchRadiusSubject.value,
    };
  }

  // Get current filters data (synchronously)
  getFiltersData(): FiltersData {
    return this.filtersDataSubject.value;
  }

  // Load filter data from backend
  private loadFilters(
    categoryIDs: number[] = [],
    positionIDs: number[] = [],
    educationLevelIDs: number[] = [],
    employmentTypeIDs: number[] = [],
    salaryFilter: SalaryFilter = { minSalary: 0, maxSalary: 0, periodKey: 'month' }
  ): void {
    this.jobService.getJobFilters(categoryIDs, positionIDs, educationLevelIDs, employmentTypeIDs, salaryFilter).subscribe({
      next: (data) => {
        this.filtersDataSubject.next({
          ...data,
          companies: data.companies || [],
        });
      },
      error: (err) => {
        console.error('Error loading filters:', err);
      },
    });
  }
}
